import type { LogContext, LoggerStrategy } from './logger-strategy.interface.js';

/**
 * A logging strategy that outputs structured JSON messages.
 * Ideal for production environments, log aggregation systems, and automated parsing.
 *
 * @example
 * ```json
 * {
 *   "timestamp": "2026-01-12T14:57:10.123Z",
 *   "level": "info",
 *   "namespace": "app:auth",
 *   "message": "user logged in",
 *   "timeDiff": 150,
 *   "data": { "userId": 123 }
 * }
 * ```
 */
export class JsonStrategy implements LoggerStrategy {
  public log(context: LogContext): void {
    const { namespace, level, timeDiff, args } = context;

    const logEntry: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      level,
      namespace,
      timeDiff,
    };

    // Parse args into message and data
    if (args.length === 0) {
      logEntry.message = '';
    } else if (args.length === 1) {
      if (typeof args[0] === 'string') {
        logEntry.message = args[0];
      } else {
        logEntry.message = '';
        logEntry.data = args[0];
      }
    } else if (typeof args[0] === 'string') {
      // Multiple args: first is message, rest is data
      logEntry.message = args[0];
      if (args.length === 2) {
        logEntry.data = args[1];
      } else {
        logEntry.data = args.slice(1);
      }
    } else {
      // Multiple args: all are data
      logEntry.message = '';
      logEntry.data = args;
    }

    // Serialize errors properly
    if (logEntry.data instanceof Error) {
      logEntry.data = {
        name: logEntry.data.name,
        message: logEntry.data.message,
        stack: logEntry.data.stack,
      };
    } else if (Array.isArray(logEntry.data)) {
      logEntry.data = logEntry.data.map((item) => {
        if (item instanceof Error) {
          return {
            name: item.name,
            message: item.message,
            stack: item.stack,
          };
        }

        return item;
      });
    }

    const consoleMethod = console[level] ?? console.log;
    consoleMethod(JSON.stringify(logEntry));
  }
}
